import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Button, 
  Card, 
  CardActions, 
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
  FormHelperText,
  Grid,
  Box,
  IconButton,
  MenuItem,
  Chip,
  SelectChangeEvent,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  Select,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton as MuiIconButton,
  Tooltip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { KeyboardArrowDown, KeyboardArrowUp, Edit, Payment, Download } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../../../../services/ApiService';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { BVTConstants } from '../../../../utils/BVTConstants';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import PayPalButton from '../../../../services/PayPalService';
import { PlatformFeePayment } from '../../../types/PlatformTypes';
import { useReports } from '../../../../hooks/useReports';
import Swal from 'sweetalert2';

function CompanyPlatformInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const { downloadReport } = useReports();
  const companyId = (location.state as { companyId?: number })?.companyId;
  const [loading, setLoading] = useState(false);
  const [activePlatformFee, setActivePlatformFee] = useState<any>(null);
  const [upcomingPlatformFee, setUpcomingPlatformFee] = useState<any>(null);
  const [openFeeDialog, setOpenFeeDialog] = useState(false);
  const [feeAmount, setFeeAmount] = useState<number>(0);
  const [frequency, setFrequency] = useState<string>('MONTHLY');
  const [feeError, setFeeError] = useState<string>('');
  const [activePaymentsExpanded, setActivePaymentsExpanded] = useState(false);
  const [upcomingPaymentsExpanded, setUpcomingPaymentsExpanded] = useState(false);

  useEffect(() => {
    fetchPlatformFeeData();
  }, []);

  const fetchPlatformFeeData = async () => {
    try {
      setLoading(true);
      const activeReq = {id: companyId || 0, status: BVTConstants.ACTIVE}
      const response = await axios.post("/getPlatformFee", activeReq);
      setActivePlatformFee(response.data);
      
      const upcomingReq = {id: companyId || 0, status: BVTConstants.UPCOMING}
      const upcomingResponse = await axios.post("/getPlatformFee", upcomingReq);
      setUpcomingPlatformFee(upcomingResponse.data);
    } catch (error) {
      console.error('Error fetching company data.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFeeDialog = () => {
    const feeToUse = upcomingPlatformFee || activePlatformFee;
    setFeeAmount(feeToUse?.amount || 0);
    setFrequency(feeToUse?.frequency || 'MONTHLY');
    setFeeError('');
    setOpenFeeDialog(true);
  };

  const handleCloseFeeDialog = () => {
    setOpenFeeDialog(false);
  };

  const handleUpdateFee = async () => {
    if (!feeAmount || feeAmount <= 0) {
      setFeeError('Amount must be greater than 0');
      return;
    }
    
    handleCloseFeeDialog();
    const feeToCompare = upcomingPlatformFee || activePlatformFee;
    if (feeAmount === feeToCompare?.amount && frequency === feeToCompare?.frequency) {
      toast.error("No changes were made to the platform fee.");
      return;
    }

    setLoading(true);
    try {
      const updatedCompanyData = {
        ...feeToCompare,
        companyId: companyId,
        amount: feeAmount,
        frequency
      };
      await axios.post("/updateCompanyPlatformFee", updatedCompanyData);
      toast.success("Platform fee updated successfully");
      fetchPlatformFeeData();
    } catch (error) {
      console.error('Error updating platform fee:', error);
      toast.error("Failed to update platform fee");
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return '0.00';
    return amount.toFixed(2);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const filterNonCancelledPayments = (payments: any[]) => {
    return payments?.filter(payment => payment?.status !== 'CANCELLED')
      .sort((a, b) => {
        // Sort by fromDate in descending order (most recent first)
        const dateA = a?.fromDate ? new Date(a.fromDate).getTime() : 0;
        const dateB = b?.fromDate ? new Date(b.fromDate).getTime() : 0;
        return dateB - dateA;
      }) || [];
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'suspended':
      case 'overdue':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const onPaymentSuccess = () => {
    Swal.fire({
      title: 'Payment Successful',
      text: 'Please Login again to see the updated status',
      icon: 'success'
    });
    fetchPlatformFeeData();
  }

  const renderActionButton = (payment: PlatformFeePayment, amount: number) => {
    if (user?.role === 'COMPANY') {
      if (payment.status === 'PENDING' || payment.status === 'OVERDUE') {
        return (
          <Tooltip title="Pay Now">
            <PayPalButton
              cart={{ type: BVTConstants.PLATFORM_FEE, refId: payment.paymentId, amount: amount }}
              onSuccess={(tx) => onPaymentSuccess()}
              onError={(err) => console.error("Error", err)}
            />
          </Tooltip>
        );
      } else if (payment.status === 'PAID') {
        return (
          <Tooltip title="Download Invoice">
            <IconButton
              onClick={() => downloadReport(
                '/generateInvoice',
                payment.invoiceId,
                `invoice-${payment.invoiceId}.pdf`
              )}
              disabled={loading}
              size="small"
              color="primary"
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        );
      }
    }
    return null;
  };

  return (
    <>
      <Stack spacing={3}>
        <Card>
          <CardHeader 
            title="Platform Fee"
            action={
              user?.role === 'ADMIN' && (
                <IconButton 
                  aria-label="edit" 
                  onClick={handleOpenFeeDialog}
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    ml: 1
                  }}
                >
                  <Edit />
                </IconButton>
              )
            }
          />
          <Divider />
          <CardContent>
            {!activePlatformFee && !upcomingPlatformFee ? (
              <Typography variant="body1" color="textSecondary" align="center">
                No platform fee setup for this company
              </Typography>
            ) : (
              <Stack spacing={4}>
                {activePlatformFee && (
                  <Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Amount
                        </Typography>
                        <Typography variant="body1">
                          {formatAmount(activePlatformFee?.amount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Effective From
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(activePlatformFee?.startDate)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Frequency
                        </Typography>
                        <Typography variant="body1">
                          {activePlatformFee?.frequency ? 
                            activePlatformFee.frequency.charAt(0) + activePlatformFee.frequency.slice(1).toLowerCase() 
                            : '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Status
                        </Typography>
                        <Chip
                            label={activePlatformFee.status}
                            color={getStatusColor(activePlatformFee.status) as any}
                            size="small"
                          />
                      </Grid>
                    </Grid>
                    <Box sx={{ position: 'relative', my: 3 }}>
                      <Divider />
                      <Box sx={{ 
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        transform: 'translateY(-50%)',
                        bgcolor: 'background.paper',
                        px: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Typography variant="subtitle1">
                          Payments
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        position: 'absolute',
                        top: '50%',
                        right: 0,
                        transform: 'translateY(-50%)',
                        bgcolor: 'background.paper',
                        px: 3
                      }}>
                        <MuiIconButton
                          onClick={() => setActivePaymentsExpanded(!activePaymentsExpanded)}
                          size="small"
                          sx={{
                            bgcolor: 'action.selected',
                            color: 'text.primary',
                            '&:hover': {
                              bgcolor: 'action.disabled'
                            }
                          }}
                        >
                          {activePaymentsExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </MuiIconButton>
                      </Box>
                    </Box>
                    <Collapse in={activePaymentsExpanded}>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>From Date</TableCell>
                              <TableCell>Till Date</TableCell>
                              <TableCell>Due Date</TableCell>
                              <TableCell>Paid Date</TableCell>
                              <TableCell>Invoice Id</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Status</TableCell>
                              {user?.role === 'COMPANY' && <TableCell align="center">Action</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filterNonCancelledPayments(activePlatformFee?.payments).length > 0 ? (
                              filterNonCancelledPayments(activePlatformFee?.payments).map((payment: any, index: number) => (
                                <TableRow key={index}>
                                  <TableCell>{formatDate(payment?.fromDate)}</TableCell>
                                  <TableCell>{formatDate(payment?.tillDate)}</TableCell>
                                  <TableCell>{formatDate(payment?.dueDate)}</TableCell>
                                  <TableCell>{payment?.paidDate ? formatDate(payment.paidDate) : 'N/A'}</TableCell>
                                  <TableCell>{payment?.invoiceId || 'N/A'}</TableCell>
                                  <TableCell>{formatAmount(activePlatformFee?.amount)}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={payment?.status || 'PENDING'} 
                                      color={payment?.status === 'PAID' ? 'success' : 'warning'} 
                                      size="small"
                                    />
                                  </TableCell>
                                  {user?.role === 'COMPANY' && (
                                    <TableCell align="center">
                                      {renderActionButton(payment, activePlatformFee?.amount)}
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={user?.role === 'COMPANY' ? 8 : 7} align="center">
                                  No payment history available
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Collapse>
                  </Box>
                )}

                {upcomingPlatformFee && (
                  <Box>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>
                      Upcoming Platform Fee
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Amount
                        </Typography>
                        <Typography variant="body1">
                          {formatAmount(upcomingPlatformFee?.amount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Effective From
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(upcomingPlatformFee?.startDate)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Frequency
                        </Typography>
                        <Typography variant="body1">
                          {upcomingPlatformFee?.frequency ? 
                            upcomingPlatformFee.frequency.charAt(0) + upcomingPlatformFee.frequency.slice(1).toLowerCase() 
                            : '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Status
                        </Typography>
                        <Chip 
                          label="Upcoming" 
                          color="info" 
                          size="small"
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ position: 'relative', my: 3 }}>
                      <Divider />
                      <Box sx={{ 
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        transform: 'translateY(-50%)',
                        bgcolor: 'background.paper',
                        px: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Typography variant="subtitle1">
                          Payments
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        position: 'absolute',
                        top: '50%',
                        right: 0,
                        transform: 'translateY(-50%)',
                        bgcolor: 'background.paper',
                        px: 3
                      }}>
                        <MuiIconButton
                          onClick={() => setUpcomingPaymentsExpanded(!upcomingPaymentsExpanded)}
                          size="small"
                          sx={{
                            bgcolor: 'action.selected',
                            color: 'text.primary',
                            '&:hover': {
                              bgcolor: 'action.disabled'
                            }
                          }}
                        >
                          {upcomingPaymentsExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </MuiIconButton>
                      </Box>
                    </Box>
                    <Collapse in={upcomingPaymentsExpanded}>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>From Date</TableCell>
                              <TableCell>Till Date</TableCell>
                              <TableCell>Due Date</TableCell>
                              <TableCell>Paid Date</TableCell>
                              <TableCell>Invoice Id</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Status</TableCell>
                              {user?.role === 'COMPANY' && <TableCell align="center">Action</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filterNonCancelledPayments(upcomingPlatformFee?.payments).length > 0 ? (
                              filterNonCancelledPayments(upcomingPlatformFee?.payments).map((payment: any, index: number) => (
                                <TableRow key={index}>
                                  <TableCell>{formatDate(payment?.fromDate)}</TableCell>
                                  <TableCell>{formatDate(payment?.tillDate)}</TableCell>
                                  <TableCell>{formatDate(payment?.dueDate)}</TableCell>
                                  <TableCell>{payment?.paidDate ? formatDate(payment.paidDate) : 'N/A'}</TableCell>
                                  <TableCell>{payment?.invoiceId || 'N/A'}</TableCell>
                                  <TableCell>{formatAmount(upcomingPlatformFee?.amount)}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={payment?.status || 'PENDING'} 
                                      color="warning" 
                                      size="small"
                                    />
                                  </TableCell>
                                  {user?.role === 'COMPANY' && (
                                    <TableCell align="center">
                                      {renderActionButton(payment, upcomingPlatformFee?.amount)}
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={user?.role === 'COMPANY' ? 8 : 7} align="center">
                                  No upcoming payments available
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Collapse>
                  </Box>
                )}
              </Stack>
            )}
          </CardContent>
        </Card>
        <LoadingSpinner loading={loading} />
      </Stack>
      <Dialog open={openFeeDialog} onClose={handleCloseFeeDialog}>
      <DialogTitle>Update Platform Fee</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2, minWidth: '300px' }}>
          <FormControl fullWidth error={!!feeError}>
            <InputLabel>Amount</InputLabel>
            <OutlinedInput
              label="Amount"
              type="number"
              value={feeAmount}
              onChange={(e) => {
                setFeeAmount(Number(e.target.value));
                setFeeError('');
              }}
              inputProps={{ min: 0, step: 0.01, precision: 2 }}
              //startAdornment={<Typography sx={{ mr: 1 }}>$</Typography>}
            />
            {feeError && <FormHelperText error>{feeError}</FormHelperText>}
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Frequency</InputLabel>
            <Select
              value={frequency}
              label="Frequency"
              onChange={(e) => setFrequency(e.target.value)}
            >
              <MenuItem value="MONTHLY">Monthly</MenuItem>
              <MenuItem value="QUARTERLY">Quarterly</MenuItem>
              <MenuItem value="YEARLY">Yearly</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseFeeDialog}>Cancel</Button>
        <Button onClick={handleUpdateFee} variant="contained">Update</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

export default CompanyPlatformInfo; 